import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import { yupResolver } from "@hookform/resolvers/yup";
import type { BudgetData } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedBudgetEditor";
import GuidedBudgetEditor from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/GuidedBudgetEditor";
import {
  appendBudgetPhaseContiguous,
  applyBudgetSegmentPatchContiguous,
  removeBudgetPhaseAndReconnect,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/budgetPhaseContiguity";
import {
  budgetDataAfterSwitchFromScheduled,
  budgetDataAfterSwitchToScheduled,
} from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/switchGuidedBudgetType";
import { wireScheduleToEventWall } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/eventWallTime";
import { useEffect, useMemo, useRef } from "react";
import type { Resolver } from "react-hook-form";
import { useForm, useWatch } from "react-hook-form";
import { scheduledBudgetHasPerSegmentFieldErrors } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/utils/scheduledBudgetRhfErrors";
import {
  EditBudgetModalSchema,
  type EditBudgetModalValidationContext,
  flattenEditBudgetModalErrors,
} from "./editBudgetModalSchema";
import { SCHEDULED_BUDGET_MAX_SEGMENTS } from "@common/utils/campaignBudgetMeta";

/**
 * Standalone campaign “Edit budget” modal (`CampaignDetail`, e.g. after opening a campaign from
 * Ads Engage). Renders {@link GuidedBudgetEditor} only — no duplicate budget UI on the Engage list
 * page itself.
 */
type EditBudgetModalProps = {
  isOpen: boolean;
  isSaving?: boolean;
  initialBudget: BudgetData;
  /** IANA zone for scheduled phase datetime-local fields. */
  eventTimezone?: string;
  /**
   * Full ad delivery window from `campaign.schedule` (run start/end, UTC ISO). Phased budget
   * phases must fall inside this window; the last phase end-wall pins to `deliveryWindowEndIso`.
   */
  scheduledBudgetDeliveryWindow?: {
    deliveryWindowStartIso: string;
    deliveryWindowEndIso: string;
  } | null;
  /** Campaign synced to Meta (`external_id`). */
  linkedToMeta?: boolean;
  onClose: () => void;
  onSave: (budget: BudgetData) => void | Promise<void>;
};

export default function EditBudgetModal({
  isOpen,
  isSaving = false,
  initialBudget,
  eventTimezone,
  scheduledBudgetDeliveryWindow,
  linkedToMeta = false,
  onClose,
  onSave,
}: EditBudgetModalProps) {
  const budgetAmountInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const validationContext = useMemo<EditBudgetModalValidationContext>(
    () => ({
      eventTimezone,
      scheduledBudgetDeliveryWindow: scheduledBudgetDeliveryWindow ?? null,
    }),
    [eventTimezone, scheduledBudgetDeliveryWindow]
  );

  const lastPhaseEndWallFromCampaignRun = useMemo(() => {
    const iso = scheduledBudgetDeliveryWindow?.deliveryWindowEndIso?.trim();
    if (!iso) return undefined;
    const tz = (eventTimezone ?? "").trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
    return wireScheduleToEventWall(iso, tz);
  }, [eventTimezone, scheduledBudgetDeliveryWindow?.deliveryWindowEndIso]);

  const {
    control,
    reset,
    trigger,
    getValues,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<BudgetData>({
    resolver: yupResolver(EditBudgetModalSchema) as unknown as Resolver<
      BudgetData,
      EditBudgetModalValidationContext
    >,
    context: validationContext,
    defaultValues: initialBudget,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const budget = useWatch({ control }) as BudgetData;

  const saveBlockedSummary =
    flattenEditBudgetModalErrors(errors) ??
    "Fix the issues above before saving (phase times, amounts, and run schedule).";

  useEffect(() => {
    if (!isOpen) return;
    reset(initialBudget);
    void trigger();
  }, [initialBudget, isOpen, reset, trigger]);

  const replaceBudget = (next: BudgetData) => {
    reset(next);
    void trigger();
  };

  const onValidSubmit = (data: BudgetData) => {
    void onSave(data);
  };

  const submit = handleSubmit(onValidSubmit, (formErrors) => {
    const msg = flattenEditBudgetModalErrors(formErrors);
    if (msg) {
      toast({
        title: msg,
        status: "error",
        isClosable: true,
      });
    }
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      isCentered
      initialFocusRef={budgetAmountInputRef}
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="xl"
        maxW={{ base: "calc(100vw - 1rem)", md: "min(1024px, calc(100vw - 3rem))" }}
        mx="auto"
      >
        <ModalHeader fontSize="lg" fontWeight="semibold" pb={2}>
          Edit budget
        </ModalHeader>
        <ModalBody
          pt={0}
          pb={2}
          px={{ base: 4, md: 6 }}
          overflowY="auto"
          maxH={{ base: "calc(100dvh - 10rem)", md: "calc(100dvh - 8rem)" }}
        >
          <VStack align="stretch" spacing={4}>
            <GuidedBudgetEditor
              eventTimezone={eventTimezone}
              scheduledBudgetDeliveryWindow={scheduledBudgetDeliveryWindow ?? null}
              amountInputRef={budgetAmountInputRef}
              budget={budget}
              linkedToMeta={linkedToMeta}
              scheduledBudgetFormErrors={
                budget?.type === "scheduled" ? errors : undefined
              }
              onChangeBudgetType={(type) => {
                const prev = getValues();
                if (type === "scheduled") {
                  replaceBudget(
                    budgetDataAfterSwitchToScheduled({
                      prev,
                      wallRangeParams: {
                        eventTimezone,
                        deliveryWindowStartIso:
                          scheduledBudgetDeliveryWindow?.deliveryWindowStartIso,
                        deliveryWindowEndIso:
                          scheduledBudgetDeliveryWindow?.deliveryWindowEndIso,
                      },
                    })
                  );
                  return;
                }
                replaceBudget(
                  budgetDataAfterSwitchFromScheduled({ prev, nextType: type })
                );
              }}
              onChangeBudgetAmount={(amount) => {
                const prev = getValues();
                replaceBudget({
                  ...prev,
                  amount: {
                    amount,
                    currency: prev.amount?.currency ?? "USD",
                  },
                });
              }}
              onAddSegment={() => {
                const prev = getValues();
                const rows = prev.segments ?? [];
                if (rows.length >= SCHEDULED_BUDGET_MAX_SEGMENTS) {
                  return;
                }
                replaceBudget({
                  ...prev,
                  segments: appendBudgetPhaseContiguous(
                    rows,
                    {
                      time_start: "",
                      time_end: "",
                      amount: {
                        amount: null,
                        currency: prev.amount?.currency ?? "USD",
                      },
                    },
                    { lastPhaseEndWall: lastPhaseEndWallFromCampaignRun }
                  ),
                });
              }}
              onRemoveSegment={(index) => {
                const prev = getValues();
                replaceBudget({
                  ...prev,
                  segments: removeBudgetPhaseAndReconnect(
                    prev.segments ?? [],
                    index,
                    { lastPhaseEndWall: lastPhaseEndWallFromCampaignRun }
                  ),
                });
              }}
              onChangeSegment={(index, patch) => {
                const prev = getValues();
                const rows = prev.segments ?? [];
                if (!rows[index]) return;
                replaceBudget({
                  ...prev,
                  segments: applyBudgetSegmentPatchContiguous(
                    rows,
                    index,
                    patch
                  ),
                });
              }}
            />
          </VStack>
        </ModalBody>
        <ModalFooter flexDirection="column" alignItems="stretch" gap={3}>
          {!isValid &&
          !(
            budget?.type === "scheduled" &&
            scheduledBudgetHasPerSegmentFieldErrors(errors)
          ) ? (
            <Text
              role="alert"
              fontSize="sm"
              color="red.600"
              lineHeight="short"
              pr={{ md: 1 }}
            >
              {saveBlockedSummary}
            </Text>
          ) : null}
          <HStack spacing={3} justify="flex-end">
            <Button variant="ghost" onClick={onClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button
              bg="gray.900"
              color="white"
              _hover={{ bg: "gray.800" }}
              onClick={() => void submit()}
              isLoading={isSaving}
              isDisabled={!isValid || isSaving}
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
